/**
 * Combien de lignes tiennent dans le cadre SANS défiler.
 *
 * Porté de `Helios/front/src/pieces.tsx` (Kayla, 05-10-2026 : « l'intérêt d'une
 * pagination, c'est de ne pas scroller vers le bas, il n'y a rien de pire sur
 * une application »), dans la forme que le cockpit a déjà :
 * `src/panels/rishfans/useLignesQuiTiennent.ts` (`{ ref, parPage, largeur }`,
 * l'arithmétique `lignesQuiTiennent` dans `panels/rishfans/modele.ts`).
 *
 * Même signature que celui de RishFans, plus deux choses :
 *   - `reserve` peut être une FONCTION, qui lit les vrais `offsetHeight` de la
 *     barre, de l'en-tête, des totaux et du pied — mesurés, pas devinés ;
 *   - `pret` : faux tant que le cadre n'a pas été mesuré, pour ne pas peindre
 *     trois lignes puis onze ; un cadre mesuré à 0 px (tests, onglet caché) est
 *     prêt et sans hauteur : le tableau montre alors toutes les lignes.
 *
 * 🪤 Pas bâti sur `useContainerSize` de core : ce hook ne mesure que par le
 * rappel du ResizeObserver, et le panneau navigateur de Claude ne rappelle
 * jamais (il ne composite pas — piège connu du skill `ecran`) : le tableau y
 * restait à zéro ligne. Ici, une mesure SYNCHRONE au montage (`useLayoutEffect`,
 * donc avant la peinture), puis l'observateur pour les redimensionnements.
 *
 * À l'intégration : fusionner avec la version RishFans (celle-ci la remplace
 * telle quelle, `reserve: number` reste accepté) et sortir `lignesQuiTiennent`
 * de `panels/rishfans/modele.ts` vers ici.
 */
import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/** Identique à `panels/rishfans/modele.ts` — une seule copie à garder. */
export function lignesQuiTiennent(
  hauteur: number,
  opts: { hauteurLigne: number; reserve: number; min?: number },
): number {
  const min = opts.min ?? 3;
  if (!Number.isFinite(hauteur) || hauteur <= 0) return min;
  return Math.max(min, Math.floor((hauteur - opts.reserve) / opts.hauteurLigne));
}

export function useLignesQuiTiennent<T extends HTMLElement = HTMLDivElement>(opts: {
  hauteurLigne: number;
  reserve: number | (() => number);
  min?: number;
}): { ref: RefObject<T | null>; parPage: number; largeur: number; hauteur: number; pret: boolean } {
  const ref = useRef<T>(null);
  // `mesure` : le cadre a été lu au moins une fois. Un cadre lu à 0 px (un
  // environnement sans mise en page, comme les tests ; un onglet caché) n'est
  // pas « pas prêt » : il est sans hauteur, et le tableau montre alors tout.
  const [taille, setTaille] = useState({ largeur: 0, hauteur: 0, mesure: false });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mesurer = () => {
      const largeur = el.clientWidth;
      const hauteur = el.clientHeight;
      setTaille((t) => (t.mesure && t.largeur === largeur && t.hauteur === hauteur ? t : { largeur, hauteur, mesure: true }));
    };
    mesurer();
    if (typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(mesurer);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // La réserve se lit au rendu : après la mesure du cadre, la barre, l'en-tête
  // et le pied sont montés, leurs `offsetHeight` sont les vrais.
  const reserve = typeof opts.reserve === "function" ? opts.reserve() : opts.reserve;
  return {
    ref,
    parPage: lignesQuiTiennent(taille.hauteur, { hauteurLigne: opts.hauteurLigne, reserve, min: opts.min }),
    largeur: taille.largeur,
    hauteur: taille.hauteur,
    pret: taille.mesure,
  };
}
